import { Check } from 'lucide-react';
import { chipMetrics, chipRowClass, chipButtonClass } from '@/components/ChipRow';
import { WmsMark, NoWmsMark } from '@/components/WmsMarks';

/**
 * ============================================================================
 * ⭐⭐⭐ THE WMS FILTER — WMS / NO WMS, BESIDE THE WORK BAR
 * ============================================================================
 *
 * > Pete, 2026-09-27: *"the WMS NO WMS search filters that follow
 * > Loading/Unloading next to status."*
 *
 * SPEC-appointment-wms-matching.md §7.1 asks for it *"beside Loading /
 * Unloading in WorkFilterBar.tsx"* — and it is a SEPARATE BAR instead, for the
 * reason that file states about itself.
 *
 * ---- ⛔⛔ WHY A THIRD BAR AND NOT TWO MORE CHIPS IN THE WORK ROW ----------
 *
 * `WorkFilterBar` exists because Pete was asked whether to fold Loading and
 * Unloading into the status chips and answered **"Separate control beside the
 * status bar."** The reason it gives is exactly the reason this is a third bar:
 *
 *   ** A SECOND QUESTION ABOUT A ROW IS A SECOND AXIS. FOLDING IT INTO THE
 *      FIRST DOES NOT CROWD THE FIRST, IT MAKES THE INTERSECTION UNREACHABLE. **
 *
 * ⛔ SELECTING TWO CHIPS IN ONE ROW IS AN **OR**. Four chips in the work row —
 * Loading, Unloading, WMS, NO WMS — would put two different questions in one
 * OR-group, and *"unloading AND no WMS"* would become unaskable. That is the
 * precise failure `WorkFilterBar` was created to avoid; repeating it one row
 * later would be a remarkable thing to do.
 *
 * ⭐ THREE BARS, THREE AXES, ANDed BY `Calendar.tsx` IN ORDER:
 *     status  — WHICH TRUCKS
 *     work    — WHAT IS HAPPENING TO THEM
 *     wms     — WHETHER WE CAN MEASURE IT
 *
 * ---- ⭐ WHY IT LOOKS IDENTICAL TO THE OTHER TWO --------------------------
 *
 * Same `chipMetrics` / `chipRowClass` / `chipButtonClass`, same `dense`, same
 * tick-in-a-swatch. ⛔ IMPORTED, NOT MATCHED BY EYE — which is the only version
 * of "consistent" that survives the next change to any of the three (§1AV.170).
 *
 * ⚠️ AND THE SWATCH IS THE MARK, NOT A COLOUR — the same rule `WorkFilterBar`
 * states: a key must look like the thing it names. These chips wear the very
 * glyph the tile wears, so a scheduler who presses NO WMS sees the mark they
 * are about to go looking for.
 *
 * ---- ⛔⛔ AMBER ON THE ABSENCE, AND IT IS NOT DECORATION ------------------
 *
 * SPEC §7.2: *"red is the no-show and the overdue, and a wall board full of red
 * that means 'not linked yet' teaches people to ignore red."*
 *   ** A WARNING SHOWN ON THE NORMAL CASE STOPS BEING A WARNING. **
 *
 * ---- ⛔ EMPTY MEANS "NO FILTER", AS IT DOES IN BOTH NEIGHBOURS -----------
 *
 * Both chips off shows everything. ⚠️ Both chips ON also shows everything, and
 * that is not a bug: they are complements, so their union is the whole board.
 * There is deliberately no "All" chip — with two options it would be a second
 * way to reach a state already one click away.
 *
 * ⚠️ AN APPOINTMENT WHOSE `hasWmsOrder` IS `null` MATCHES NEITHER CHIP. The
 * board queries always compute it, so this should not arise there; if it ever
 * does, the honest behaviour is that a truck nobody asked about is not claimed
 * by either answer.
 *   ** NOT ASKED IS NOT THE SAME ANSWER AS NONE. **
 */

/** ⛔ THE CODES ARE THE CONTRACT. `Calendar.tsx` filters on these strings. */
export const WMS_FILTER_KINDS = [
    { code: 'HAS_WMS', label: 'WMS' },
    { code: 'NO_WMS',  label: 'No WMS' },
] as const;

export interface WmsFilterBarProps {
    /** Codes currently selected. ⛔ EMPTY MEANS "no filter", not "show nothing". */
    wmsFilter: Set<string>;
    /** Toggle one code. Owned by `Calendar.tsx`, like `toggleStatus`. */
    toggleWms: (code: string) => void;
    /**
     * ⭐ Per-code counts over the **UNFILTERED** range — the same rule both
     * neighbours document at length. ⛔ A count computed from what survived the
     * filter cannot tell you what the filter is hiding, which is the only
     * question it is on screen to answer.
     */
    wmsCounts: Record<string, number>;
    /** Tighter sizing for the board header strip. */
    dense?: boolean;
    className?: string;
}

export function WmsFilterBar({
    wmsFilter,
    toggleWms,
    wmsCounts,
    dense = false,
    className,
}: WmsFilterBarProps) {
    const { swatch, tick, ring } = chipMetrics(dense);
    return (
        <div className={chipRowClass('status', dense, className)}>
            {WMS_FILTER_KINDS.map(({ code, label }) => {
                const checked = wmsFilter.has(code);
                const absent = code === 'NO_WMS';
                /* ⚠️ THE TOOLTIP STATES THE PREDICATE, because the chip cannot.
                   "No WMS" does not say whether it means "we never looked" or
                   "we looked and found nothing", and that is the first thing
                   somebody will want to know. */
                const title = checked
                    ? `Stop filtering by ${label}`
                    : absent
                        ? 'Show only appointments with no matching WMS order'
                        : 'Show only appointments that have a matching WMS order';
                /* ⭐ AMBER FOR THE ABSENCE, EMERALD FOR THE PRESENCE — and the
                   same fill-not-brightness selection device both neighbours
                   settled on after Pete found the dimmed version unreadable:
                   on is a solid disc with a white tick, off is the mark itself
                   on no fill. ** WHEN THE SAME VISUAL DEVICE MEANS BOTH
                   NOT-SELECTED AND NOT-AVAILABLE, EVERY UNSELECTED CONTROL
                   LOOKS BROKEN. ** */
                const onClass  = absent ? 'bg-amber-600 text-white'   : 'bg-emerald-600 text-white';
                const offClass = absent ? 'text-amber-600'            : 'text-emerald-600';
                const btnClass = checked
                    ? (absent ? 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                              : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100')
                    : 'hover:bg-muted';
                const Mark = absent ? NoWmsMark : WmsMark;
                return (
                    <button
                        key={code}
                        type="button"
                        onClick={() => toggleWms(code)}
                        title={title}
                        className={chipButtonClass('status', dense, btnClass)}
                    >
                        <span
                            className={`inline-flex ${swatch} items-center justify-center rounded-full ${
                                checked ? `${onClass} ${ring}` : offClass
                            }`}
                        >
                            {checked
                                ? <Check className={tick} strokeWidth={3} />
                                : (
                                    <Mark
                                        wrapClassName="relative inline-flex h-full w-full"
                                        markClassName="absolute inset-0 h-full w-full"
                                        ringStrokeWidth={4.5}
                                    />
                                )}
                        </span>
                        {label}{dense
                            ? <span className="tabular-nums">{wmsCounts[code] ?? 0}</span>
                            : ` (${wmsCounts[code] ?? 0})`}
                    </button>
                );
            })}
        </div>
    );
}

export default WmsFilterBar;
